import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { AgentGate, AgentTurnAction, ViewResponse } from '@app/shared/models/agent-chat.models';
import {
  AiChatContinueTurn,
  AiChatOption,
  AiChatSelectValue,
  gateQuestionBelongsInThread,
} from '@app/shared/models/agent-chat.models';

export const REVIEW_AND_SEARCH_PROMPT = 'Go ahead and run the search now with what we have.';
// The wire label the harness owns (results/copy.py BACK_TO_ACTIONS_LABEL); match + emit via these only.
export const BACK_TO_ACTIONS_PROMPT = 'Back to actions';
// Matches the select components' own default, so an absent suggestion changes nothing.
const DEFAULT_CUSTOM_VALUE_PLACEHOLDER = 'Type your own';
// The compare picker is the one multi-select gate with a real ceiling: the harness accepts
// 2-3 (results/copy.py _COMPARE_PICKER_HEADER) and bounces anything else back with an error.
const COMPARE_PICKER_HEADER = 'Choose candidates';
const COMPARE_MAX_SELECTIONS = 3;

function isBackToActionsLabel(label: string): boolean {
  return label.trim().toLowerCase() === BACK_TO_ACTIONS_PROMPT.toLowerCase();
}

// The harness lightly rewrites punctuation between the streamed ask and the committed gate
// question, so compare on letters and digits only.
const asComparable = (text: string | null | undefined): string =>
  (text ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

@Component({
  selector: 'fastr-ui-chat-interaction',
  templateUrl: './chat-interaction.component.html',
  styleUrls: ['./chat-interaction.component.scss'],
})
export class ChatInteractionComponent implements OnChanges {
  readonly REFINE_PROMPT =
    "Go ahead and fill in the gaps: propose sensible defaults only for what we haven't covered — keep everything I've stated as is — then run the search now.";
  @Input() response: ViewResponse | null = null;
  @Output() continueTurn = new EventEmitter<AiChatContinueTurn>();

  @Input() showSecondaryActions = true;
  /** The ask this turn already streamed into the bubble; its gate question must not re-type. */
  @Input() streamedQuestion: string | null = null;
  animateQuestion = true;
  readonly CLARIFY_OPTIONS_GATE_KIND = 'clarify_options';
  readonly FEEDBACK_REVIEW_GATE_KIND = 'feedback_review';
  isSingleSelectGate = false;
  showBackAction = false;
  isMultiSelectGate = false;
  isSimpleInputGate = false;
  simpleInputQuestion = '';
  simpleInputPlaceholder = 'Type your answer...';
  // A gate's free-text box: the harness sends a typed-answer example in `suggestions` (e.g. the
  // pickers' "Compare #5 with #19", which reaches past the five options they can render).
  customValuePlaceholder = DEFAULT_CUSTOM_VALUE_PLACEHOLDER;
  multiSelectOptions: AiChatOption[] = [];
  multiSelectMaxSelections = 0;
  singleSelectOptions: { label: string; description: string }[] = [];

  ngOnChanges(): void {
    const gate = this.response?.gate;

    // A gate-less turn and an option-less gate other than open_question keep their text in the
    // thread, so the panel is a bare input box and must not repeat it.
    this.simpleInputQuestion =
      !gate || gateQuestionBelongsInThread(gate) ? '' : this.getSimpleInputQuestion(gate);
    const streamed = asComparable(this.streamedQuestion);
    this.animateQuestion = !streamed || streamed !== asComparable(gate?.question);

    const isClarifyOptionsGate =
      gate?.kind === this.CLARIFY_OPTIONS_GATE_KIND && (gate.options?.length ?? 0) > 0;
    this.isMultiSelectGate = isClarifyOptionsGate && !!gate?.multi_select;
    this.isSingleSelectGate = isClarifyOptionsGate && !gate?.multi_select;
    // A gate earns the Back button only when it actually offers the option — the root
    // actions menu does not, so a Back there would just reopen the same menu.
    const gateOffersBack =
      isClarifyOptionsGate &&
      (gate?.options ?? []).some((option) => isBackToActionsLabel(option.label));
    const hasResultsToReturnTo = (this.response?.reranked_results?.length ?? 0) > 0;
    this.showBackAction =
      gateOffersBack ||
      (!gate && this.response?.current_phase === 'feedback_gathering' && hasResultsToReturnTo);
    this.singleSelectOptions = this.visibleOptions(gate, this.showBackAction);
    this.multiSelectOptions = this.singleSelectOptions.map((option, index) => ({
      id: `${index}-${option.label}`,
      label: option.label,
    }));
    this.multiSelectMaxSelections =
      gate?.header?.trim() === COMPARE_PICKER_HEADER ? COMPARE_MAX_SELECTIONS : 0;
    this.isSimpleInputGate =
      !this.isMultiSelectGate &&
      !this.isSingleSelectGate &&
      gate?.kind !== this.FEEDBACK_REVIEW_GATE_KIND;
    this.simpleInputPlaceholder = this.placeholderFor('Type your answer...');
    this.customValuePlaceholder = this.placeholderFor(DEFAULT_CUSTOM_VALUE_PLACEHOLDER);
  }

  onMultiSelectSubmit(value: AiChatSelectValue): void {
    const prompt = this.formatMultiSelectPrompt(value);
    if (prompt) {
      const selected = value.selectedOptions.map((o) => o.label);
      const other = value.customValue?.trim();
      this.continueTurn.emit({
        prompt,
        resumeConfig: { selected, other },
        selection: value,
      });
    }
  }

  onBack(): void {
    const prompt = BACK_TO_ACTIONS_PROMPT;
    if (!this.response?.gate) {
      this.continueTurn.emit({ prompt, useMessageTurn: true });
      return;
    }
    this.continueTurn.emit({
      prompt,
      resumeConfig: { selected: [], other: prompt },
    });
  }

  onSimpleInputSubmit(prompt: string): void {
    if (!prompt) {
      return;
    }

    if (this.response?.gate === null || this.response?.gate === undefined) {
      this.continueTurn.emit({ prompt, useMessageTurn: true });
    } else {
      this.continueTurn.emit({ prompt, resumeConfig: { text: prompt } });
    }
  }
  onReviewAndSearch(): void {
    this.emitActionTurn(REVIEW_AND_SEARCH_PROMPT, 'review_and_search');
  }

  onRefine(): void {
    this.emitActionTurn(this.REFINE_PROMPT, 'refine_then_review');
  }

  private emitActionTurn(prompt: string, action: AgentTurnAction): void {
    if (this.response?.gate === null || this.response?.gate === undefined) {
      this.continueTurn.emit({ prompt, useMessageTurn: true, action });
    } else {
      this.continueTurn.emit({
        prompt,
        resumeConfig: { selected: [], other: prompt },
        action,
      });
    }
  }

  private visibleOptions(
    gate: AgentGate | null | undefined,
    hideBackAction: boolean,
  ): { label: string; description: string }[] {
    return (gate?.options ?? []).filter(
      (option) => !(hideBackAction && isBackToActionsLabel(option.label)),
    );
  }

  private getSimpleInputQuestion(gate: AgentGate | null | undefined): string {
    if (gate?.question) {
      return gate.question;
    }

    return (
      this.response?.messages
        ?.slice()
        .reverse()
        .find((message) => message.role === 'assistant')?.content || ''
    );
  }

  private placeholderFor(fallback: string): string {
    const suggestion = this.getSimpleInputSuggestion();
    return suggestion ? `e.g. ${suggestion}` : fallback;
  }

  private getSimpleInputSuggestion(): string {
    const responseWithSuggestion = this.response as (ViewResponse & { suggestion?: string }) | null;
    return (
      responseWithSuggestion?.suggestion?.trim() ||
      this.response?.suggestions?.find((suggestion) => suggestion.trim())?.trim() ||
      ''
    );
  }

  private formatMultiSelectPrompt(value: AiChatSelectValue): string {
    const selectedLabels = value.selectedOptions.map((option) => option.label);
    const customValue = value.customValue?.trim();
    const selectedPrompt = selectedLabels.join(', ');

    if (selectedPrompt && customValue) {
      return `${selectedPrompt}; ${customValue}`;
    }

    return selectedPrompt || customValue || '';
  }

  onSingleSelectSubmit(value: AiChatSelectValue): void {
    const prompt = this.formatSingleSelectPrompt(value);
    if (prompt) {
      const selected = value.selectedOptions.map((o) => o.label);
      const other = value.customValue?.trim();
      this.continueTurn.emit({
        prompt,
        resumeConfig: { selected, other },
        selection: value,
        showSelectionSummary: false,
      });
    }
  }

  private formatSingleSelectPrompt(value: AiChatSelectValue): string {
    if (value.selectedOptions.length) {
      const selectedLabels = value.selectedOptions.map((option) => option.label);
      return selectedLabels.join('\n');
    }
    return value.customValue?.trim() || '';
  }
}
